import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const SERVICE_OPTIONS = [
  "Full Refurbishment",
  "Painting & Decorating",
  "Bathroom Installation",
  "Kitchen Installation",
  "End of Tenancy Cleaning",
  "Something else",
];

const FIELD_CLASS =
  "w-full bg-transparent border border-white/15 rounded-[2px] px-5 py-4 text-[#f5f5f0] placeholder:text-[#a3a3a3]/60 text-base outline-none focus:border-[#c5832b] transition-colors duration-300";

const initial = { name: "", email: "", phone: "", service: "", message: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service) {
      toast.error("Please add your name, email and the service you need.");
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes`,
        form
      );
      toast.success("Request received — we'll be in touch within one working day.", {
        icon: <CheckCircle2 size={18} className="text-[#c5832b]" />,
      });
      setForm(initial);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong — please try again or call us."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-24 md:py-32 bg-[#1a1a1a] border-t border-white/10">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow className="mb-4">Free, no obligation</Eyebrow>
            <h2 className="font-serif text-4xl md:text-6xl text-[#f5f5f0] leading-tight mb-8">
              Let's talk about
              <br />
              <em className="italic text-[#c5832b]">your space.</em>
            </h2>
            <p className="text-[#a3a3a3] text-base md:text-lg leading-relaxed mb-10 max-w-md">
              Tell us a little about the work you have in mind. We'll come
              round, look at the job properly, and give you a clear fixed
              quote — usually within the week.
            </p>
            <div className="space-y-4 text-[#f5f5f0]">
              <p data-testid="contact-phone" className="text-lg">
                <span className="text-[#a3a3a3] text-sm block mb-1">Call us</span>
                07792724398
              </p>
              <p data-testid="contact-email" className="text-lg">
                <span className="text-[#a3a3a3] text-sm block mb-1">Email us</span>
                preecerefurbishments@gmail.com
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form
              data-testid="quote-form"
              onSubmit={submit}
              className="bg-[#121212] border border-white/10 rounded-[2px] p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div>
                <label htmlFor="q-name" className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                  Name *
                </label>
                <input
                  id="q-name"
                  data-testid="quote-name-input"
                  className={FIELD_CLASS}
                  placeholder="Your name"
                  value={form.name}
                  onChange={set("name")}
                />
              </div>
              <div>
                <label htmlFor="q-email" className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                  Email *
                </label>
                <input
                  id="q-email"
                  data-testid="quote-email-input"
                  type="email"
                  className={FIELD_CLASS}
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={set("email")}
                />
              </div>
              <div>
                <label htmlFor="q-phone" className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                  Phone
                </label>
                <input
                  id="q-phone"
                  data-testid="quote-phone-input"
                  type="tel"
                  className={FIELD_CLASS}
                  placeholder="Best number to reach you"
                  value={form.phone}
                  onChange={set("phone")}
                />
              </div>
              <div>
                <label htmlFor="q-service" className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                  Service needed *
                </label>
                <select
                  id="q-service"
                  data-testid="quote-service-select"
                  className={`${FIELD_CLASS} appearance-none ${form.service ? "text-[#f5f5f0]" : "text-[#a3a3a3]/60"}`}
                  value={form.service}
                  onChange={set("service")}
                >
                  <option value="" disabled>
                    Choose a service…
                  </option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="q-message" className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
                  About the job
                </label>
                <textarea
                  id="q-message"
                  data-testid="quote-message-textarea"
                  rows={5}
                  className={`${FIELD_CLASS} resize-none`}
                  placeholder="Which rooms? Rough timing? Anything we should know…"
                  value={form.message}
                  onChange={set("message")}
                />
              </div>
              <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <p className="text-xs text-[#a3a3a3]/70">
                  No spam, no pushy follow-ups. Just a proper quote.
                </p>
                <button
                  type="submit"
                  data-testid="quote-submit-button"
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-3 bg-[#c5832b] hover:bg-[#d9953c] disabled:opacity-60 text-[#121212] font-semibold px-10 py-4 rounded-[2px] transition-colors duration-300"
                >
                  {sending ? (
                    <>
                      Sending <Loader2 size={18} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Request my quote <Send size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
